import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ReactFlow,
  Controls,
  Background,
  applyNodeChanges,
  applyEdgeChanges,
  addEdge,
  ReactFlowProvider,
  useReactFlow,
  type Node as RFNode,
  type Edge as RFEdge,
  type NodeChange,
  type EdgeChange,
  type Connection,
  type ReactFlowInstance,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { useNavigate } from 'react-router-dom';
import { HelpCircle } from 'lucide-react';
import { driver } from 'driver.js';
import 'driver.js/dist/driver.css';
import TriggerNode from '@/components/automation/nodes/TriggerNode';
import MessageNode from '@/components/automation/nodes/MessageNode';
// import AINode from '@/components/automation/nodes/AINode';
import MenuNode from '@/components/automation/nodes/MenuNode';
import LiveAgentNode from '@/components/automation/nodes/LiveAgentNode';
import EndNode from '@/components/automation/nodes/EndNode';
import Sidebar from '@/components/automation/Sidebar';
import TestPanel from '@/components/automation/TestPanel';
import toast from 'react-hot-toast';
import { getAutomation, saveAutomation, setAutomationActive, getAutomationStatus } from '@/services/automationService';
import { getIntents } from '@/services/openaiService';

const nodeTypes = {
  trigger: TriggerNode,
  message: MessageNode,
  // ai: AINode,
  menu: MenuNode,
  'live-agent': LiveAgentNode,
  end: EndNode,
};

type ChatMessage = { sender: 'bot' | 'user'; text: string };
type MenuOption = { id: string; label: string; value: string };
type FlowViewport = { x: number; y: number; zoom: number };
type Flow = { nodes?: RFNode[]; edges?: RFEdge[]; viewport?: FlowViewport };

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null;
}

function extractFlow(resp: unknown): Flow | null {
  if (isRecord(resp)) {
    if ('nodes' in resp || 'edges' in resp) {
      return resp as unknown as Flow;
    }
    const dataVal = (resp as Record<string, unknown>)['data'];
    if (isRecord(dataVal)) {
      return dataVal as unknown as Flow;
    }
  }
  return null;
}

function getWelcomeMessage(node: RFNode): string | undefined {
  const data = node.data as Record<string, unknown> | undefined;
  const val = data ? data['welcomeMessage'] : undefined;
  return typeof val === 'string' ? val : undefined;
}

function getGoodbyeMessage(node: RFNode): string | undefined {
  const data = node.data as Record<string, unknown> | undefined;
  const val = data ? data['goodbyeMessage'] : undefined;
  return typeof val === 'string' ? val : undefined;
}

function getMenuIntro(node: RFNode): string | undefined {
  const data = node.data as Record<string, unknown> | undefined;
  const val = data ? data['introMessage'] : undefined;
  return typeof val === 'string' ? val : undefined;
}

function getMenuOptions(node: RFNode): MenuOption[] {
  const data = node.data as Record<string, unknown> | undefined;
  const opts = data ? (data['options'] as unknown) : undefined;
  if (Array.isArray(opts)) {
    return opts
      .filter((o) => isRecord(o) && typeof o['id'] === 'string')
      .map((o) => ({
        id: String((o as Record<string, unknown>)['id']),
        label: String((o as Record<string, unknown>)['label'] ?? ''),
        value: String((o as Record<string, unknown>)['value'] ?? ''),
      }));
  }
  return [];
}

const initialNodes: RFNode[] = [
  {
    id: '1',
    type: 'trigger',
    position: { x: 250, y: 5 },
    data: {},
  },
];

const initialEdges: RFEdge[] = [];

type LinearChatbotFlowProps = {
  channelId: string;
  onActiveChange?: (active: boolean) => void;
};

function FlowInner({ channelId, onActiveChange }: LinearChatbotFlowProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const reactFlowWrapper = useRef<HTMLDivElement | null>(null);
  const { toObject, setViewport } = useReactFlow();
  const [nodes, setNodes] = useState<RFNode[]>(initialNodes);
  const [edges, setEdges] = useState<RFEdge[]>(initialEdges);
  const [reactFlowInstance, setReactFlowInstance] = useState<
    ReactFlowInstance<RFNode, RFEdge> | null
  >(null);
  const [simulationState, setSimulationState] = useState({
    history: [] as string[],
    currentNodeId: null as string | null,
    chatMessages: [] as ChatMessage[],
    isWaitingForUserInput: true,
  });
  const [isTestPanelOpen, setIsTestPanelOpen] = useState(false);
  const idRef = useRef<number>(2);
  const [isActive, setIsActive] = useState(false);

  const cleanEdges = useCallback((nodesArr: RFNode[], edgesArr: RFEdge[]) => {
    const nodeMap = new Map(nodesArr.map((n) => [n.id, n]));
    const validMenuHandles = new Map<string, Set<string>>();
    for (const n of nodesArr) {
      if (n.type === 'menu') {
        const set = new Set(getMenuOptions(n).map((o) => String(o.id)));
        validMenuHandles.set(n.id, set);
      }
    }
    return (edgesArr || []).filter((e) => {
      if (!nodeMap.has(e.source) || !nodeMap.has(e.target)) return false;
      if (e.sourceHandle && validMenuHandles.has(e.source)) {
        const set = validMenuHandles.get(e.source)!;
        if (!set.has(String(e.sourceHandle))) return false;
      }
      return true;
    });
  }, []);

  useEffect(() => {
    const fetchAutomation = async () => {
      if (!channelId) return;
      try {
        const resp = await getAutomation(channelId);
        const flow = extractFlow(resp);
        if (flow) {
          const nodesLoaded = flow.nodes || [];
          setNodes(nodesLoaded);
          setEdges(cleanEdges(nodesLoaded, flow.edges || []));
          if (flow.viewport) setViewport(flow.viewport);
          const maxNum = nodesLoaded
            .map((n) => parseInt(String(n.id), 10))
            .filter((v) => Number.isFinite(v))
            .reduce((a: number, b: number) => Math.max(a, b), 1);
          idRef.current = (Number.isFinite(maxNum) ? maxNum : 1) + 1;
        }
  const status = (await getAutomationStatus(channelId)) as unknown as { isActive?: boolean } | null;
  setIsActive(Boolean(status?.isActive));
      } catch (error) {
        console.error('Error fetching automation flow:', error);
      }
    };
    fetchAutomation();
  }, [channelId, setViewport, cleanEdges]);

  const onNodesChange = useCallback(
    (changes: NodeChange[]) => setNodes((nds) => applyNodeChanges(changes, nds)),
    [setNodes],
  );

  const onEdgesChange = useCallback(
    (changes: EdgeChange[]) => setEdges((eds) => applyEdgeChanges(changes, eds)),
    [setEdges],
  );

  const onEdgesDelete = useCallback(() => {}, []);

  const onConnect = useCallback(
    (connection: Connection) => setEdges((eds) => addEdge(connection, eds)),
    [setEdges],
  );

  const onDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const onNodesDelete = useCallback(
    (deleted: RFNode[]) => {
      setEdges((eds) => eds.filter((e) => !deleted.some((n) => e.source === n.id || e.target === n.id)));
    },
    [setEdges],
  );

  const onDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();
      const type = event.dataTransfer.getData('application/reactflow');
      if (typeof type === 'undefined' || !type) return;
      if (!reactFlowInstance) return;
      const position = reactFlowInstance.screenToFlowPosition({ x: event.clientX, y: event.clientY });
      let data: Record<string, unknown> = {};
      switch (type) {
        case 'ai':
          data = { intents: [], mode: 'intents' };
          break;
        case 'menu':
          data = { options: [] };
          break;
        case 'live-agent':
          data = {};
          break;
        case 'end':
          data = { goodbyeMessage: '' };
          break;
        default:
          data = { message: '' };
      }
      const newNode: RFNode = { id: `${idRef.current++}`, type, position, data };
      setNodes((nds) => nds.concat(newNode));
    },
    [reactFlowInstance],
  );

  const onSave = useCallback(async () => {
    if (!reactFlowInstance) return;
    if (!channelId) {
      toast.error(t('channelIdMissing'));
      return;
    }
    const flow = toObject();
    await saveAutomation(channelId, flow);
    toast.success(t('flowSavedSuccessfully'));
  }, [reactFlowInstance, toObject, channelId, t]);

  useEffect(() => {
    const traverseFlow = async () => {
      if (simulationState.isWaitingForUserInput || !simulationState.currentNodeId) return;

      let currentNode: RFNode | undefined = nodes.find((n) => n.id === simulationState.currentNodeId);
      const newHistory: string[] = [...simulationState.history];
      const newChatMessages: ChatMessage[] = [...simulationState.chatMessages];
      let nextNodeToWaitForInput: string | null = null;
      let shouldStop = false;

      while (currentNode && !shouldStop) {
        const outgoingEdges = edges.filter((edge) => edge.source === currentNode!.id);

        if (currentNode.type === 'message') {
          if (outgoingEdges.length > 0) {
            const nextNodeId = outgoingEdges[0].target;
            newHistory.push(nextNodeId);
            currentNode = nodes.find((node) => node.id === nextNodeId);
          } else {
            currentNode = undefined;
          }
        } else if (currentNode.type === 'live-agent') {
          newChatMessages.push({ sender: 'bot', text: t('liveAgentMessage') });
          currentNode = undefined;
        } else if (currentNode.type === 'end') {
          const goodbye = getGoodbyeMessage(currentNode);
          if (goodbye) newChatMessages.push({ sender: 'bot', text: goodbye });
          currentNode = undefined;
        } else {
          nextNodeToWaitForInput = currentNode.id;
          if (currentNode.type === 'menu') {
            const intro = getMenuIntro(currentNode) || '';
            const menuOptions = getMenuOptions(currentNode)
              .map((opt) => `${opt.value}. ${opt.label}`)
              .join('\n');
            if (intro || menuOptions) {
              newChatMessages.push({ sender: 'bot', text: `${intro}${intro && menuOptions ? '\n\n' : ''}${menuOptions}` });
            }
          }
          shouldStop = true;
        }
      }

      setSimulationState((prev) => ({
        ...prev,
        history: newHistory,
        chatMessages: newChatMessages,
        currentNodeId: nextNodeToWaitForInput,
        isWaitingForUserInput: true,
      }));
    };
    traverseFlow();
  }, [simulationState.currentNodeId, simulationState.isWaitingForUserInput, simulationState.history, simulationState.chatMessages, nodes, edges, t]);

  const handleResetSimulation = useCallback(() => {
    setSimulationState({ currentNodeId: null, chatMessages: [], history: [], isWaitingForUserInput: true });
    setEdges((eds) => eds.map((edge) => ({ ...edge, animated: false, style: { stroke: '#b1b1b7' } })));
  }, [setEdges]);

  const handleSendMessage = useCallback(
    async (message: string) => {
      const newChatMessages: ChatMessage[] = [...simulationState.chatMessages, { sender: 'user', text: message }];
      let currentId: string | null = simulationState.currentNodeId;
      const newHistory: string[] = [...simulationState.history];
      let nextNodeId: string | null = null;

      if (!currentId) {
        const triggerNode = nodes.find((node) => node.type === 'trigger');
        if (!triggerNode) {
          toast.error(t('flowMustHaveTrigger'));
          return;
        }
        const trigId = triggerNode.id;
        currentId = trigId;
        newHistory.push(trigId);
        const welcome = getWelcomeMessage(triggerNode);
        if (welcome) newChatMessages.push({ sender: 'bot', text: welcome });
        const outgoingEdges = edges.filter((edge) => edge.source === currentId);
        if (outgoingEdges.length > 0) nextNodeId = outgoingEdges[0].target;
      } else {
        const currentNode = nodes.find((node) => node.id === currentId);
        if (currentNode) {
          const outgoingEdges = edges.filter((edge) => edge.source === currentNode.id);
          if (currentNode.type === 'ai') {
            const intentsVal = (currentNode.data as Record<string, unknown> | undefined)?.['intents'] as unknown;
            const intentNames = Array.isArray(intentsVal)
              ? intentsVal.map((intent) => (isRecord(intent) ? String(intent['name'] ?? '') : ''))
              : [];
            const detectedIntentName = await getIntents(message, intentNames);
            const detectedIntent = Array.isArray(intentsVal)
              ? intentsVal.find((intent) => isRecord(intent) && intent['name'] === detectedIntentName)
              : undefined;
            const edge = detectedIntent ? outgoingEdges.find((e) => e.sourceHandle === detectedIntent.id) : null;
            if (edge) nextNodeId = edge.target;
          } else if (currentNode.type === 'menu') {
            const option = getMenuOptions(currentNode).find((opt) => opt.value === message);
            const edge = option ? outgoingEdges.find((e) => e.sourceHandle === String(option.id)) : null;
            if (edge) nextNodeId = edge.target;
          }
        }
      }

      if (nextNodeId) {
        newHistory.push(nextNodeId);
        const nextNode = nodes.find((node) => node.id === nextNodeId);
        if (nextNode && nextNode.type === 'message') {
          const data = nextNode.data as Record<string, unknown> | undefined;
          const msg = data ? data['message'] : undefined;
          if (typeof msg === 'string') newChatMessages.push({ sender: 'bot', text: msg });
        }
        setSimulationState({ history: newHistory, chatMessages: newChatMessages, currentNodeId: nextNodeId, isWaitingForUserInput: false });
      } else {
        newChatMessages.push({ sender: 'bot', text: t('dontUnderstand') });
        setSimulationState((prev) => ({ ...prev, chatMessages: newChatMessages, isWaitingForUserInput: true }));
      }
    },
    [nodes, edges, simulationState, t],
  );

  const startTour = useCallback(() => {
    const driverObj = driver({
      showProgress: true,
      showButtons: ['next', 'previous', 'close'],
      steps: [
        {
          popover: {
            title: t('linearChatbotTourTitle'),
            description: t('linearChatbotTourDescription'),
            side: 'bottom',
            align: 'start'
          }
        },
        {
          element: '#flow-sidebar',
          popover: {
            title: t('tourSidebarTitle'),
            description: t('tourSidebarDescription'),
            side: 'right',
            align: 'start'
          }
        },
        {
          element: '.react-flow',
          popover: {
            title: t('tourCanvasTitle'),
            description: t('tourCanvasDescription'),
            side: 'left',
            align: 'start'
          }
        },
        {
          element: '#test-button',
          popover: {
            title: t('tourTestButtonTitle'),
            description: t('tourTestButtonDescription'),
            side: 'bottom',
            align: 'end'
          }
        },
        {
          element: '#save-flow-button',
          popover: {
            title: t('tourSaveFlowButtonTitle'),
            description: t('tourSaveFlowButtonDescription'),
            side: 'bottom',
            align: 'center'
          }
        },
        {
          element: '#activate-flow-button',
          popover: {
            title: t('tourActivateFlowButtonTitle'),
            description: t('tourActivateFlowButtonDescription'),
            side: 'bottom',
            align: 'start'
          }
        },
        {
          element: '.react-flow__controls',
          popover: {
            title: t('tourControlsTitle'),
            description: t('tourControlsDescription'),
            side: 'left',
            align: 'center'
          }
        }
      ]
    });
    
    driverObj.drive();
  }, [t]);

  const highlightedEdges = useMemo(() => {
    const historyNodeIds = new Set(simulationState.history);
    return edges.map((edge) => {
      const isHighlighted = historyNodeIds.has(edge.source) && historyNodeIds.has(edge.target);
      return { ...edge, animated: isHighlighted, style: { ...edge.style, stroke: isHighlighted ? 'green' : '#b1b1b7' } };
    });
  }, [edges, simulationState.history]);

  return (
    <div className="flex h-[calc(100vh-100px)]" ref={reactFlowWrapper}>
      <Sidebar />
      <ReactFlow
        nodes={nodes}
        edges={highlightedEdges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onEdgesDelete={onEdgesDelete}
        onConnect={onConnect}
        edgesFocusable
        deleteKeyCode={['Delete', 'Backspace']}
        onNodesDelete={onNodesDelete}
        onDrop={onDrop}
        onDragOver={onDragOver}
        nodeTypes={nodeTypes}
        onInit={(instance) => setReactFlowInstance(instance as ReactFlowInstance<RFNode, RFEdge>)}
        fitView
      >
        <div className="absolute top-4 left-4 z-10 flex gap-2">
          <button
            onClick={() => navigate(`/channel/${channelId}`)}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-green-100 flex items-center gap-2 border border-gray-300"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
            {t('backToSettings')}
          </button>
          <button
            onClick={startTour}
            className="px-4 py-2 bg-blue-50 text-blue-700 border border-blue-200 rounded-md hover:bg-blue-100 flex items-center gap-2"
            title={t('startTour')}
          >
            <HelpCircle size={18} />
            {t('startTour')}
          </button>
        </div>
        <div className="absolute top-4 right-4 z-10 flex gap-2">
          <button id="test-button" onClick={() => setIsTestPanelOpen(true)} className="px-4 py-2 bg-green-500 text-white rounded-md">
            {t('test')}
          </button>
          <button id="save-flow-button" onClick={onSave} className="px-4 py-2 bg-blue-500 text-white rounded-md">
            {t('save')}
          </button>
      <button
            id="activate-flow-button"
            onClick={async () => {
              const next = !isActive;
              try {
                await setAutomationActive(channelId, next);
                setIsActive(next);
                onActiveChange?.(next);
              } catch (e) {
                const err = e as { response?: { data?: { message?: string } } };
                const msg: string = err?.response?.data?.message || t('couldNotChangeStatus');
                alert(msg);
              }
            }}
            className={`px-4 py-2 rounded-md ${isActive ? 'bg-red-600 text-white' : 'bg-indigo-600 text-white'}`}
          >
            {isActive ? t('deactivateLinearBot') : t('activateLinearBot')}
          </button>
        </div>
        {isTestPanelOpen && (
          <TestPanel
            chatMessages={simulationState.chatMessages}
            onSendMessage={handleSendMessage}
            onReset={handleResetSimulation}
            onClose={() => setIsTestPanelOpen(false)}
          />
        )}
        <Controls />
        <Background />
      </ReactFlow>
    </div>
  );
}

export default function LinearChatbotFlow({ channelId, onActiveChange }: LinearChatbotFlowProps) {
  return (
    <ReactFlowProvider>
  <FlowInner channelId={channelId} onActiveChange={onActiveChange} />
    </ReactFlowProvider>
  );
}
